import { Component, Inject, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ClientAccountPermissions } from '@app/core/permissions';
import { NavigationService } from '@app/core/services/navigation/navigation.service';
import { ToastService } from '@app/core/services/toast/toast.service';
import { BuildTypeLogicService } from '@app/logic/build-type';
import { ClientAccountLogicService } from '@app/logic/client-account/client-account.logic.service';
import { ILeadLotInterestDto } from '@app/logic/leads/interfaces/i.lead.lot.interest.dto';
import { ILeadMappedItem } from '@app/logic/leads/interfaces/i.lead.mapped';
import { BaseDialogFormViewDirective } from '@app/shared/base-views/base-dialog-form-view.directive';
import { CbDialogService } from '@app/shared/components/dialog/cb-dialog.service';
import { FormMode } from '@app/shared/enums/form';
import {
    AccountTypeEnumId,
    LotTypeEnumId,
    LotContractTypeEnumId,
    MarketStatusEnumId,
    ACCOUNT_TYPE_ENUM,
    LOT_TYPE_ENUM,
    LOT_CONTRACT_TYPE_ENUM,
    MARKET_STATUS_ENUM,
    IClientAccountDto
} from '@classictechsolutions/hubapi-transpiled-enums';
import { IEnumLookup } from '@classictechsolutions/typescriptenums';
import { IQualifyLeadDto } from '@classictechsolutions/hubapi-transpiled-enums/build/main/lib/dtos/QualifyLeadDto';
import { take } from 'rxjs';

interface IData {
    mappedItem: ILeadMappedItem;
}

@Component({
    selector: 'cb-lead-qualify-design-build-dialog',
    templateUrl: './lead-qualify-design-build-dialog.component.html',
    styleUrls: ['./lead-qualify-design-build-dialog.component.scss'],
    standalone: false
})
export class LeadQualifyDesignBuildDialogComponent extends BaseDialogFormViewDirective<any, any, any> implements OnInit {
    public static readonly MIN_WIDTH = '700px';

    public mappedItem: ILeadMappedItem;
    public qualifyLeadForm: NgForm;
    public selectedLots: number[] = [];
    public accountMode = 'create';
    public accountTypes: IEnumLookup<AccountTypeEnumId>[];
    public lotTypes: IEnumLookup<LotTypeEnumId>[];
    public lotContractTypes: IEnumLookup<LotContractTypeEnumId>[];
    public buildTypes: any[];
    public marketStatus: IEnumLookup<MarketStatusEnumId>[];
    public data: any;
    public selectedAccount: IClientAccountDto;

    public designAndBuildIsPrimary: boolean;

    public readonly accountSearchParams = { status: ['open', 'locked'] };

    constructor(
        private readonly accountLogic: ClientAccountLogicService,
        private readonly buildTypeLogic: BuildTypeLogicService,
        private readonly navigationService: NavigationService,
        public readonly dialogRef: MatDialogRef<LeadQualifyDesignBuildDialogComponent>,
        @Inject(CbDialogService) public readonly cbDialog: CbDialogService,
        @Inject(MAT_DIALOG_DATA) public readonly dta: IData,
        public toastService: ToastService,
        private readonly clientAccountPermissions: ClientAccountPermissions
    ) {
        super(dialogRef, toastService);
        this.formMode = FormMode.Edit;
        this.mappedItem = dta.mappedItem;
        this.accountTypes = ACCOUNT_TYPE_ENUM.toLookup();
        this.lotTypes = LOT_TYPE_ENUM.toLookup();
        this.lotContractTypes = LOT_CONTRACT_TYPE_ENUM.toLookup();
        this.buildTypeLogic
            .$getList()
            .pipe(take(1)).subscribe(x => this.buildTypes = x);
        this.marketStatus = MARKET_STATUS_ENUM.toLookup();
    }

    public ngOnInit(): void {

        this.data = {
            lotIds: []
        } as IQualifyLeadDto;

        const primaryDb: ILeadLotInterestDto = this.mappedItem.getReadOnlyPrimaryInterest();
        if (!primaryDb || primaryDb.contractType !== LOT_CONTRACT_TYPE_ENUM.DesignAndBuild || !primaryDb.lotId) {
            // this.formHelper.expectedError('Primary Design & Build Lot Interest Not Found');
        } else {
            this.data.lotIds = [primaryDb.lotId];
        }
    }

    public formatAddress(address): string {
        return address ? address.street + ', ' + address.suburb : '';
    }

    public selectedAccountChanged(): void {
        if (this.selectedAccount) {
            this.accountMode = 'view';
            this.data.clientAccountId = this.selectedAccount.id;
            return;
        }
        this.accountMode = 'create';
        // this.data.clientAccountId = undefined;
        // irdNumber = " " - needed for inputmask, because it does not accept undefined
        this.data.clientAccount = { irdNumber: ' ' };
    }

    public nameOnBlur(): void {
        if (this.data.clientAccount) {
            if (this.data.clientAccount.legalName === '' || this.data.clientAccount.legalName === undefined) {
                this.data.clientAccount.legalName = this.data.clientAccount.name;
            }
        }
    }

    public canCreate(): boolean {
        return this.clientAccountPermissions.canCreate();
    }

    public hasLot(): boolean {
        return this.data.lotIds.length > 0;
    }

    public filterInterests = (leadInterest: ILeadLotInterestDto): ILeadLotInterestDto => {
        return leadInterest.contractType === LOT_CONTRACT_TYPE_ENUM.HouseAndLand && leadInterest;
    };

    public createClientAccountChange(): void {
        if (this.data.createNewClientAccount) {
            this.selectedAccount = undefined;
            this.data.clientAccountId = undefined;
        } else {
            this.data.clientAccountId = this.selectedAccount.id;
        }
    }

    public save(): void {
        if (!this.hasLot()) {
            // this.formHelper.expectedError('Cannot Save - Primary Design & Build Lot Interest Not Found');
            return;
        }
        this.createClientAccountChange();

        this.cbDialog.confirm({
            dialogHeading: 'Qualify Lead',
            message: 'Are you sure you want to qualify this lead?',
            confirmed: () => {
                this.mappedItem.qualify(this.data).pipe(take(1)).subscribe((qualifyResult) => {
                    this.handleNext({ qualifyResult, qualifiedLotIds: this.data.lotIds });
                });
            }
        });
    }

}
